import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Camera, ArrowRight } from "lucide-react";
import { useBusiness } from "@/context/BusinessContext";

/**
 * Phase 25 — Homepage gallery carousel strip.
 * Pulls photos flagged in_carousel=true from /api/gallery and pans them in
 * a smooth horizontal scroller. Self-hides when there are no photos.
 *
 * Re-fetches whenever the current business changes so multi-tenant
 * preview navigations (?biz=<slug>) show the right tenant's photos.
 */
export default function GalleryCarousel() {
  const [photos, setPhotos] = useState([]);
  const { business } = useBusiness();
  const bid = business?.id;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/gallery?in_carousel=true&limit=12");
        if (!cancelled) setPhotos(data.items || []);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [bid]);

  if (!photos.length) return null;

  return (
    <section className="bg-[#FDFBF7] py-14 sm:py-20" data-testid="gallery-carousel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] font-medium text-stone-500 inline-flex items-center gap-2">
              <Camera className="w-3.5 h-3.5" /> From the gallery
            </p>
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-2">
              Snaps from our regulars
            </h2>
          </div>
          <Link
            to="/gallery"
            data-testid="gallery-carousel-more"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm text-stone-700 hover:text-stone-900"
          >
            See all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 no-scrollbar">
          {photos.map((p) => (
            <figure
              key={p.id}
              data-testid={`gallery-carousel-${p.id}`}
              className="snap-start shrink-0 w-[72%] sm:w-72 rounded-2xl overflow-hidden bg-white border border-stone-100 relative group"
            >
              <img
                src={p.image_data_url || p.public_url}
                alt={p.caption || "Gallery"}
                className="w-full aspect-square object-cover transition-transform group-hover:scale-[1.03] select-none"
                loading="lazy"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
              />
              {(p.caption || p.customer_name) && (
                <figcaption className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/70 via-black/30 to-transparent text-white">
                  {p.caption && <div className="text-sm leading-tight line-clamp-2">{p.caption}</div>}
                  {p.customer_name && <div className="text-[10px] uppercase tracking-widest opacity-80 mt-1">— {p.customer_name}</div>}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
